/**
 * uploadPipeline —— 共享上传管线工具
 *
 * 从 BatchUploadModal (原 ImageUploadModal) 抽出，统一「压缩 + 并发池 + XHR 字节级进度上传」，
 * 供各上传入口复用，避免各写一套 compress/fetch 逻辑（P2-10）。
 *
 * 关键点：
 *  - xhrUpload 用 XMLHttpRequest 的 upload.onprogress 回调真实字节进度，
 *    回调签名 onProgress(batchLoaded, batchTotal)，调用方按 batch 汇总为
 *    sum(loaded)/sum(total)，并发乱序也不会回跳（P3-15）。
 *  - fetch 无法拿到上传方向的进度，故主路径改用 XHR。
 */

// ─── 上传完成提示音（单声·清晰·约 0.4s）────────────────────────────────────
// 用 Web Audio 合成一声清脆的"叮"（E6, 1318Hz），不依赖音频文件。
// 供 BatchUploadModal / ProxyUploadModal 在上传全部完成时调用。
export function playUploadDone() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const play = () => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 1318.51; // E6 —— 清脆明亮，不刺耳
      const t = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02); // 快速起音
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.40); // 0.4s 衰减
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.42);
      setTimeout(() => { try { ctx.close(); } catch {} }, 700);
    };
    if (ctx.state === 'suspended') ctx.resume().then(play).catch(() => {});
    else play();
  } catch { /* 音频不可用时静默 */ }
}

// ─── 压缩单张图片（已禁用，直接返回原始文件）────────────────────────────────
// OCR 识别率和图片质量正相关，任何 JPEG 压缩都会降低识别率（尤其是细字、深色背景）。
// 石榴AI API 支持最大 20MB，手机截图原图通常 500KB~5MB，无需压缩。
// 保留此函数签名供调用方复用，内部直接返回原文件。
export async function compressImage(file) {
  return file;
}

// ─── 并发任务池 ──────────────────────────────────────────────────────────────
// tasks: Array<() => Promise<any>>，concurrency: 同时最多运行数
export async function runPool(tasks, concurrency) {
  const queue   = [...tasks];
  const workers = Math.min(concurrency, tasks.length);
  const results = [];
  async function worker() {
    while (queue.length) {
      const task = queue.shift();
      if (task) results.push(await task());
    }
  }
  await Promise.all(Array.from({ length: workers }, worker));
  return results;
}

// ─── XHR 上传（真实字节级进度）─────────────────────────────────────────────
// 参数：
//   url       上传地址
//   files     File[]（该批）
//   token     Bearer token
//   fieldName 表单字段名（默认 images）
//   headers   额外请求头（如 X-Upload-Batch-Id）
//   timeout   毫秒
//   onProgress(batchLoaded, batchTotal) 上传进度回调（字节，按 batch 维度）
// 返回：resolve(responseText) / reject(Error)，Error.status 带 HTTP 状态码
export function xhrUpload({ url, files, token, fieldName = 'images', headers = {}, timeout = 90000, onProgress }) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    files.forEach(f => fd.append(fieldName, f));

    const batchTotal = files.reduce((s, f) => s + f.size, 0);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.timeout = timeout;

    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      // e.lengthComputable 为真时用真实 loaded；否则回落以避免进度停滞
      const loaded = e.lengthComputable ? Math.min(e.loaded, batchTotal) : 0;
      onProgress(loaded, batchTotal);
    };
    xhr.upload.onloadend = () => {
      // 上传方向结束：把该批进度补满，避免个别浏览器最后一帧不触发 progress
      if (onProgress) onProgress(batchTotal, batchTotal);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText);
      } else {
        const err = new Error(`server ${xhr.status}`);
        err.status = xhr.status;
        err.responseText = xhr.responseText; // 带上后端返回的 JSON，供调用方读取具体原因
        reject(err);
      }
    };
    xhr.onerror   = () => reject(new Error('network'));
    xhr.ontimeout = () => reject(new Error('timeout'));
    xhr.send(fd);
  });
}
